import {
  Progress,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@shigo-ui/ui';
import { dashboardT } from '../../../../lib/messages';
import { formatCurrency, formatNumber, formatUsd } from '../../../../lib/format';
import type { DashboardDemandPartnerRow, DashboardDateRange } from '../../model';

export function DashboardRevenueCapCell({
  row,
  range,
}: {
  row: DashboardDemandPartnerRow;
  range: DashboardDateRange;
}) {
  if (!row.revenueCapEnabled || !row.revenueCap || row.revenueCap <= 0) return '-';
  if (range.dateFrom !== range.dateTo) {
    return (
      <div className="space-y-1 tabular-nums">
        <div>{formatCurrency(row.revenue)}</div>
        <div className="text-muted-foreground text-xs whitespace-nowrap">
          {dashboardT('dashboard.table.dailyCap', {
            cap: formatUsd(row.revenueCap, { maximumFractionDigits: 0 }),
          })}
        </div>
      </div>
    );
  }
  const revenue = row.revenue ?? 0;
  const percentage = (revenue / row.revenueCap) * 100;
  let tone = '[&>div]:bg-primary';
  if (percentage >= 100) tone = '[&>div]:bg-destructive';
  else if (percentage >= 80) tone = '[&>div]:bg-warning';
  const label = `${dashboardT('dashboard.table.revenueCap')} ${formatNumber(percentage, 0)}%`;
  const description = `${formatNumber(percentage, 0)}%`;
  return (
    <Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={dashboardT('dashboard.table.revenueCap')}
              // my-0! overrides the table's in-cell `[&_button]:-my-2`, which would let the
              // comparison line below overlap the progress bar.
              className="text-foreground focus-visible:ring-ring my-0! block w-full min-w-0 cursor-pointer space-y-1 rounded-sm text-left tabular-nums focus-visible:ring-2 focus-visible:outline-none"
            >
              <span className="block truncate text-xs whitespace-nowrap">
                {formatUsd(revenue, { maximumFractionDigits: 0 })} /{' '}
                {formatUsd(row.revenueCap, { maximumFractionDigits: 0 })}
              </span>
              <Progress
                value={percentage}
                aria-label={label}
                aria-valuetext={label}
                className={`${tone} bg-muted h-1 w-32 max-w-full`}
              />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>{description}</TooltipContent>
      </Tooltip>
      <PopoverContent aria-label={label} className="w-auto p-2 text-xs" align="start">
        {description}
      </PopoverContent>
    </Popover>
  );
}
